import React, {
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react'
import { AimOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Input, Select, Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import DeviceHistoryModal from '../components/display/DeviceHistoryModal'
import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../components/common/FilterBar'
import { PageLayout } from '../components/layout'
import {
    buildAllFilterOptions,
    getAllAwareDisplayValues,
    normalizeAllAwareValues,
    type FilterSelectValue,
} from '../components/statistics/filterSelectUtils'
import {
    isDevicePackageKey,
    type DevicePackageKey,
} from '../constants/devicePackages'
import { usePermissions } from '../hooks/usePermissions'
import {
    type DeviceDistributionQuery,
    useDistributionData,
    useDistributionOptions,
} from '../services/deviceMosquitoService.ts'
import { usePestTypeOptions } from '../services/pestTypeOptionsService'
import {
    type DeviceDistributionClusterItem,
    type DeviceDistributionDeviceOption,
    type DeviceDistributionDeviceItem,
    type DeviceDistributionItem,
} from '../types/device-mosquito'
import {
    buildConfiguredPestTypeFilterOptions,
    mergeMosquitoDisplayCounts,
    normalizeConfiguredDisplayCategory,
} from '../utils/mosquitoDisplay'
import { localeService } from '../services/localeService'
import { loadTiandituApi } from '../utils/tianditu'
import './DeviceMosquito.css'

interface DeviceMosquitoLocationState {
    refreshMapAt?: unknown
}

interface TiandituLngLat {
    lng: number
    lat: number
}

type TiandituPoint = unknown

interface TiandituPixelPoint {
    x?: number
    y?: number
    getX?: () => number
    getY?: () => number
}

interface TiandituBounds {
    getSouthWest?: () => TiandituLngLat
    getNorthEast?: () => TiandituLngLat
}

type TiandituIcon = unknown

interface TiandituMarker {
    addEventListener(eventName: 'click', handler: () => void): void
    setZIndexOffset?(offset: number): void
}

interface TiandituLabel {
    addEventListener?(eventName: 'click', handler: () => void): void
    setStyle?(style: Record<string, string | number>): void
}

type TiandituOverlay = TiandituMarker | TiandituLabel

interface TiandituMap {
    centerAndZoom(point: TiandituLngLat, zoom: number): void
    addControl(control: unknown): void
    addOverLay(overlay: TiandituOverlay): void
    removeOverLay(overlay: TiandituOverlay): void
    addEventListener?(
        eventName: 'click' | 'moveend' | 'zoomend',
        handler: () => void
    ): void
    getZoom?(): number
    getBounds?(): TiandituBounds
    lngLatToContainerPoint?(point: TiandituLngLat): TiandituPixelPoint
    lngLatToLayerPoint?(point: TiandituLngLat): TiandituPixelPoint
    pointToOverlayPixel?(point: TiandituLngLat): TiandituPixelPoint
    checkResize?(): void
    enableDrag?(): void
    disableDrag?(): void
    enableScrollWheelZoom?(): void
    enableDoubleClickZoom?(): void
    enableKeyboard?(): void
    disableInertia?(): void
    disableInertialDragging?(): void
    disableDraggingInertia?(): void
}

interface TiandituNamespace {
    Map: new (container: HTMLDivElement) => TiandituMap
    LngLat: new (lng: number, lat: number) => TiandituLngLat
    Marker: new (
        point: TiandituLngLat,
        options?: { icon?: TiandituIcon }
    ) => TiandituMarker
    Icon?: new (config: {
        iconUrl: string
        iconSize: TiandituPoint
        iconAnchor: TiandituPoint
    }) => TiandituIcon
    Label?: new (config: {
        text: string
        position: TiandituLngLat
        offset: TiandituPoint
    }) => TiandituLabel
    Point: new (x: number, y: number) => TiandituPoint
    Control: {
        MapType: new () => unknown
        Zoom: new () => unknown
    }
}

interface TiandituMapTypeControlWithLayerSorting {
    Di: () => unknown
    di: () => unknown
    Fi: () => unknown[]
    Si: () => unknown[]
}

interface MarkerOverlay {
    marker: TiandituMarker
    label: TiandituLabel | null
}

interface SelectedSummaryPosition {
    left: number
    top: number
    placement: 'left' | 'right'
}

interface FiltersState {
    projectIds: number[]
    deviceIds: string[]
    pestTypes: string[]
}

interface MapViewport {
    minLng?: number
    maxLng?: number
    minLat?: number
    maxLat?: number
    zoom?: number
}

type MapStatus = 'loading' | 'ready' | 'error'
type DeviceSelectionSource = 'map' | 'list'
type DeviceDistributionDisplayDevice = Omit<
    DeviceDistributionDeviceItem,
    'lng' | 'lat'
> & {
    lng?: number | null
    lat?: number | null
}
type DeviceHistorySnapshot = {
    id: string
    type?: number | null
    package_key?: string | null
    purchase_version?: string | null
    supports_species_recognition?: boolean | null
    name?: string | null
    address?: string | null
    device_code?: string | null
    project_id?: number | null
}

interface DeviceDistributionListRow {
    item: DeviceDistributionDisplayDevice
    isSelected: boolean
    canLocateDevice: boolean
    baseLabel: string
    regionLabel: string
    lastReportText: string
    detectedCountText: string
}

interface DeviceDistributionListItemProps extends DeviceDistributionListRow {
    locateText: string
    noCoordinatesText: string
    itemRef?: React.Ref<HTMLButtonElement>
    onSelect: (item: DeviceDistributionDisplayDevice) => void
}

interface MapDragGestureState {
    pointerId: number
    startX: number
    startY: number
    lastX: number
    lastY: number
    startTime: number
    startCenter: TiandituLngLat
    startZoom: number
}

interface MapFlingRestoreTarget {
    center: TiandituLngLat
    zoom: number
}

const DEFAULT_MAP_ZOOM = 10
const DEVICE_FOCUS_ZOOM = 14
const VIEWPORT_REFRESH_DEBOUNCE_MS = 500
const MARKER_RENDER_BATCH_SIZE = 50
const DEVICE_LIST_ROW_HEIGHT = 116
const DEVICE_LIST_ROW_GAP = 10
const DEVICE_LIST_ROW_STRIDE = DEVICE_LIST_ROW_HEIGHT + DEVICE_LIST_ROW_GAP
const DEVICE_LIST_OVERSCAN = 4
const DEVICE_LIST_SEARCH_DEBOUNCE_MS = 120
const VIEWPORT_BOUNDS_EPSILON = 0.0001
const SELECTED_DEVICE_MARKER_Z_INDEX = 2000
const DEFAULT_MAP_CENTER = {
    lng: 113.75793,
    lat: 23.01781,
}
const MAP_SUMMARY_CARD_WIDTH = 320
const MAP_SUMMARY_CARD_HEIGHT = 194
const MAP_SUMMARY_EDGE_PADDING = 12
const MAP_SUMMARY_MARKER_GAP = 16
const MAP_LARGE_FLING_MIN_DISTANCE_PX = 720
const MAP_LARGE_FLING_MAX_DURATION_MS = 220
const MAP_LARGE_FLING_MIN_SPEED_PX_PER_MS = 3.0
const SELECTED_DEVICE_MARKER_ICON_URL = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41"><path d="M12.5 1.5C6.15 1.5 1 6.65 1 13c0 8.15 11.5 26.5 11.5 26.5S24 21.15 24 13C24 6.65 18.85 1.5 12.5 1.5Z" fill="#ff7a00" stroke="#ffffff" stroke-width="2"/></svg>'
)}`

const createDefaultFilters = (): FiltersState => ({
    projectIds: [],
    deviceIds: [],
    pestTypes: [],
})

const PACKAGE_KEY_BY_VERSION_NAME: Record<string, DevicePackageKey> = {
    '进阶侦测版': 'foundation',
    '专业甄别版': 'recognition',
    '全能大师版': 'insight',
}

const resolveDevicePackageKey = (
    packageKey?: string | null,
    purchaseVersion?: string | null,
    supportsSpeciesRecognition?: boolean | null
): DevicePackageKey | null => {
    if (isDevicePackageKey(packageKey)) {
        return packageKey
    }

    const normalizedPurchaseVersion =
        typeof purchaseVersion === 'string' ? purchaseVersion.trim() : ''
    if (normalizedPurchaseVersion in PACKAGE_KEY_BY_VERSION_NAME) {
        return PACKAGE_KEY_BY_VERSION_NAME[normalizedPurchaseVersion]
    }

    if (supportsSpeciesRecognition === false) {
        return 'foundation'
    }

    if (supportsSpeciesRecognition === true) {
        return 'insight'
    }

    return null
}

const arraysEqual = <T,>(left: T[], right: T[]) =>
    left.length === right.length &&
    left.every((value, index) => value === right[index])

const viewportsEqual = (left: MapViewport, right: MapViewport) => {
    const zoomEqual = (left.zoom ?? DEFAULT_MAP_ZOOM) === (right.zoom ?? DEFAULT_MAP_ZOOM)
    const boundsEqual = (['minLng', 'maxLng', 'minLat', 'maxLat'] as const).every(
        (key) => {
            const leftValue = left[key]
            const rightValue = right[key]

            if (leftValue == null && rightValue == null) {
                return true
            }

            if (leftValue == null || rightValue == null) {
                return false
            }

            return Math.abs(leftValue - rightValue) < VIEWPORT_BOUNDS_EPSILON
        }
    )

    return zoomEqual && boundsEqual
}

const getTianditu = () =>
    (window as Window & { T?: unknown }).T as unknown as
        | TiandituNamespace
        | undefined

const createSelectedDeviceMarkerIcon = (
    tianditu: TiandituNamespace
): TiandituIcon | undefined => {
    if (typeof tianditu.Icon !== 'function') {
        return undefined
    }

    return new tianditu.Icon({
        iconUrl: SELECTED_DEVICE_MARKER_ICON_URL,
        iconSize: new tianditu.Point(25, 41),
        iconAnchor: new tianditu.Point(12, 41),
    })
}

const createDefaultMapCenterPoint = (tianditu: TiandituNamespace) =>
    new tianditu.LngLat(DEFAULT_MAP_CENTER.lng, DEFAULT_MAP_CENTER.lat)

const hasMapTypeLayerSorting = (
    control: unknown
): control is TiandituMapTypeControlWithLayerSorting => {
    const candidate = control as Partial<TiandituMapTypeControlWithLayerSorting>

    return (
        typeof candidate.Di === 'function' &&
        typeof candidate.di === 'function' &&
        typeof candidate.Fi === 'function' &&
        typeof candidate.Si === 'function'
    )
}

const createMapTypeControl = (tianditu: TiandituNamespace): unknown => {
    const control = new tianditu.Control.MapType()

    if (hasMapTypeLayerSorting(control)) {
        control.Si = function (this: TiandituMapTypeControlWithLayerSorting) {
            return [this.Di(), this.di(), ...this.Fi()]
        }
    }

    return control
}

const syncMapContainerSize = (map: TiandituMap | null) => {
    map?.checkResize?.()
}

const enableMapInteractions = (map: TiandituMap) => {
    map.enableDrag?.()
    map.enableScrollWheelZoom?.()
    map.enableDoubleClickZoom?.()
    map.enableKeyboard?.()
}

const disableMapFlingInertia = (map: TiandituMap) => {
    map.disableInertia?.()
    map.disableInertialDragging?.()
    map.disableDraggingInertia?.()
}

const isPointerOutsideElement = (
    event: PointerEvent,
    element: HTMLElement | null
) => {
    if (!element) {
        return false
    }

    const rect = element.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) {
        return false
    }

    return (
        event.clientX < rect.left ||
        event.clientX >= rect.right ||
        event.clientY < rect.top ||
        event.clientY >= rect.bottom
    )
}

const normalizeLngLat = (
    point: TiandituLngLat | undefined
): TiandituLngLat | null => {
    if (
        !point ||
        typeof point.lng !== 'number' ||
        !Number.isFinite(point.lng) ||
        typeof point.lat !== 'number' ||
        !Number.isFinite(point.lat)
    ) {
        return null
    }

    return point
}

const readMapViewport = (map: TiandituMap | null): MapViewport => {
    if (!map) {
        return { zoom: DEFAULT_MAP_ZOOM }
    }

    const viewport: MapViewport = {
        zoom: map.getZoom?.() ?? DEFAULT_MAP_ZOOM,
    }
    const bounds = map.getBounds?.()
    const southWest = normalizeLngLat(bounds?.getSouthWest?.())
    const northEast = normalizeLngLat(bounds?.getNorthEast?.())

    if (southWest && northEast) {
        viewport.minLng = Math.min(southWest.lng, northEast.lng)
        viewport.maxLng = Math.max(southWest.lng, northEast.lng)
        viewport.minLat = Math.min(southWest.lat, northEast.lat)
        viewport.maxLat = Math.max(southWest.lat, northEast.lat)
    }

    return viewport
}

const getMapCenter = (map: TiandituMap | null): TiandituLngLat | null => {
    const bounds = map?.getBounds?.()
    const southWest = normalizeLngLat(bounds?.getSouthWest?.())
    const northEast = normalizeLngLat(bounds?.getNorthEast?.())

    if (!southWest || !northEast) {
        return null
    }

    return {
        lng: (southWest.lng + northEast.lng) / 2,
        lat: (southWest.lat + northEast.lat) / 2,
    }
}

const isLargeFlingGesture = (gesture: MapDragGestureState, endTime: number) => {
    const distance = Math.hypot(
        gesture.lastX - gesture.startX,
        gesture.lastY - gesture.startY
    )
    const duration = Math.max(1, endTime - gesture.startTime)
    const speed = distance / duration

    return (
        distance >= MAP_LARGE_FLING_MIN_DISTANCE_PX &&
        duration <= MAP_LARGE_FLING_MAX_DURATION_MS &&
        speed >= MAP_LARGE_FLING_MIN_SPEED_PX_PER_MS
    )
}

const buildDistributionQuery = (
    filters: FiltersState,
    viewport: MapViewport
): DeviceDistributionQuery => ({
    ...viewport,
    projectIds: filters.projectIds,
    deviceIds: filters.deviceIds,
    pestTypes: filters.pestTypes,
})

const getDeviceBaseLabel = (record: DeviceDistributionDisplayDevice) =>
    record.label?.trim() ||
    record.name?.trim() ||
    record.address?.trim() ||
    record.device_code?.trim() ||
    record.id

const normalizeDeviceSearchText = (
    value: number | string | null | undefined
) => String(value ?? '').trim().toLocaleLowerCase()

const deviceMatchesKeyword = (
    record: DeviceDistributionDisplayDevice,
    keyword: string
) => {
    const tokens = keyword.split(/\s+/).filter(Boolean)

    if (tokens.length === 0) {
        return true
    }

    const searchText = [
        record.label,
        record.name,
        record.address,
        record.device_code,
        record.id,
        record.project_name,
        record.region_name,
        record.region_label,
    ]
        .map(normalizeDeviceSearchText)
        .join(' ')

    return tokens.every((token) => searchText.includes(token))
}

const hasValidCoordinates = (
    item: DeviceDistributionItem
): item is DeviceDistributionItem & { lng: number; lat: number } =>
    typeof item.lng === 'number' &&
    Number.isFinite(item.lng) &&
    item.lng >= -180 &&
    item.lng <= 180 &&
    typeof item.lat === 'number' &&
    Number.isFinite(item.lat) &&
    item.lat >= -90 &&
    item.lat <= 90

const hasValidDeviceCoordinates = (
    item: DeviceDistributionDisplayDevice
): item is DeviceDistributionDeviceItem =>
    typeof item.lng === 'number' &&
    Number.isFinite(item.lng) &&
    item.lng >= -180 &&
    item.lng <= 180 &&
    typeof item.lat === 'number' &&
    Number.isFinite(item.lat) &&
    item.lat >= -90 &&
    item.lat <= 90

const clampNumber = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max)

const readPixelPoint = (
    point: TiandituPixelPoint | null | undefined
): { x: number; y: number } | null => {
    if (!point) {
        return null
    }

    const x = typeof point.x === 'number' ? point.x : point.getX?.()
    const y = typeof point.y === 'number' ? point.y : point.getY?.()

    if (
        typeof x !== 'number' ||
        !Number.isFinite(x) ||
        typeof y !== 'number' ||
        !Number.isFinite(y)
    ) {
        return null
    }

    return { x, y }
}

const getMapContainerPoint = (
    map: TiandituMap,
    container: HTMLDivElement,
    lngLat: TiandituLngLat
): { x: number; y: number } | null => {
    const projectedPoint =
        readPixelPoint(map.lngLatToContainerPoint?.(lngLat)) ??
        readPixelPoint(map.lngLatToLayerPoint?.(lngLat)) ??
        readPixelPoint(map.pointToOverlayPixel?.(lngLat))

    if (projectedPoint) {
        return projectedPoint
    }

    const bounds = map.getBounds?.()
    const southWest = normalizeLngLat(bounds?.getSouthWest?.())
    const northEast = normalizeLngLat(bounds?.getNorthEast?.())
    const rect = container.getBoundingClientRect()

    if (
        !southWest ||
        !northEast ||
        rect.width <= 0 ||
        rect.height <= 0 ||
        southWest.lng === northEast.lng ||
        southWest.lat === northEast.lat
    ) {
        return null
    }

    const minLng = Math.min(southWest.lng, northEast.lng)
    const maxLng = Math.max(southWest.lng, northEast.lng)
    const minLat = Math.min(southWest.lat, northEast.lat)
    const maxLat = Math.max(southWest.lat, northEast.lat)

    return {
        x: ((lngLat.lng - minLng) / (maxLng - minLng)) * rect.width,
        y: ((maxLat - lngLat.lat) / (maxLat - minLat)) * rect.height,
    }
}

const getSelectedSummaryPosition = (
    map: TiandituMap | null,
    container: HTMLDivElement | null,
    item: DeviceDistributionDisplayDevice | null
): SelectedSummaryPosition | null => {
    if (!map || !container || !item || !hasValidDeviceCoordinates(item)) {
        return null
    }

    const rect = container.getBoundingClientRect()
    const point = getMapContainerPoint(map, container, {
        lng: item.lng,
        lat: item.lat,
    })

    if (!point || rect.width <= 0 || rect.height <= 0) {
        return null
    }

    const cardWidth = Math.min(
        MAP_SUMMARY_CARD_WIDTH,
        Math.max(220, rect.width - MAP_SUMMARY_EDGE_PADDING * 2)
    )
    const cardHeight = Math.min(
        MAP_SUMMARY_CARD_HEIGHT,
        Math.max(128, rect.height - MAP_SUMMARY_EDGE_PADDING * 2)
    )
    const rightLeft = point.x + MAP_SUMMARY_MARKER_GAP
    const canPlaceRight =
        rightLeft + cardWidth <= rect.width - MAP_SUMMARY_EDGE_PADDING
    const left = canPlaceRight
        ? rightLeft
        : point.x - MAP_SUMMARY_MARKER_GAP - cardWidth
    const maxLeft = Math.max(
        MAP_SUMMARY_EDGE_PADDING,
        rect.width - cardWidth - MAP_SUMMARY_EDGE_PADDING
    )
    const maxTop = Math.max(
        MAP_SUMMARY_EDGE_PADDING,
        rect.height - cardHeight - MAP_SUMMARY_EDGE_PADDING
    )
    const isPointInsideViewport =
        point.x >= 0 &&
        point.x <= rect.width &&
        point.y >= 0 &&
        point.y <= rect.height

    if (!isPointInsideViewport) {
        return {
            left,
            top: point.y - cardHeight / 2,
            placement: canPlaceRight ? 'right' : 'left',
        }
    }

    return {
        left: clampNumber(left, MAP_SUMMARY_EDGE_PADDING, maxLeft),
        top: clampNumber(
            point.y - cardHeight / 2,
            MAP_SUMMARY_EDGE_PADDING,
            maxTop
        ),
        placement: canPlaceRight ? 'right' : 'left',
    }
}

const isDeviceItem = (
    item: DeviceDistributionItem
): item is DeviceDistributionDeviceItem => item.type === 'device'

const requiresSpeciesRecognitionFilter = (pestTypes: string[]) =>
    pestTypes.some(
        (pestType) =>
            normalizeConfiguredDisplayCategory(pestType) !== 'otherMosquito'
    )

const getVisibleMosquitoCount = (
    pestTypeCounts: Record<string, number>,
    totalCount: number,
    selectedPestTypes: string[],
    supportsSpeciesRecognition = true
) => {
    if (selectedPestTypes.length === 0) {
        return totalCount
    }

    if (
        !supportsSpeciesRecognition &&
        requiresSpeciesRecognitionFilter(selectedPestTypes)
    ) {
        return 0
    }

    const preciseCount = selectedPestTypes.reduce(
        (sum, pestType) => sum + (pestTypeCounts[pestType] ?? 0),
        0
    )
    if (preciseCount > 0) {
        return preciseCount
    }

    const displayCounts = mergeMosquitoDisplayCounts(pestTypeCounts)
    return selectedPestTypes.reduce(
        (sum, pestType) => sum + (displayCounts[pestType] ?? 0),
        0
    )
}

const matchesSelectedProjects = (
    projectId: number | null | undefined,
    selectedProjectIds: number[]
) =>
    selectedProjectIds.length === 0 ||
    (projectId != null && selectedProjectIds.includes(projectId))

const normalizeProjectFilterValues = (
    selectedOptionIds: number[],
    filterIdsByOptionId: Map<number, number[]>
) => {
    if (selectedOptionIds.length === 0) {
        return []
    }

    return Array.from(
        new Set(
            selectedOptionIds.flatMap(
                (projectId) => filterIdsByOptionId.get(projectId) ?? [projectId]
            )
        )
    )
}

const sumCounts = (counts: Record<string, number>) =>
    Object.values(counts).reduce((sum, count) => sum + count, 0)

const filterPestCountsForDisplay = (
    counts: Record<string, number>,
    selectedPestTypes: string[],
    supportsSpeciesRecognition = true
) => {
    if (selectedPestTypes.length === 0) {
        return counts
    }

    if (
        !supportsSpeciesRecognition &&
        requiresSpeciesRecognitionFilter(selectedPestTypes)
    ) {
        return {}
    }

    const preciseCounts = Object.fromEntries(
        selectedPestTypes
            .map((pestType) => [pestType, counts[pestType] ?? 0] as const)
            .filter(([, count]) => count > 0)
    )
    if (Object.keys(preciseCounts).length > 0) {
        return preciseCounts
    }

    const displayCounts = mergeMosquitoDisplayCounts(counts)
    return Object.fromEntries(
        selectedPestTypes
            .map((pestType) => [pestType, displayCounts[pestType] ?? 0] as const)
            .filter(([, count]) => count > 0)
    )
}

const buildDeviceFromOption = (
    option: DeviceDistributionDeviceOption,
    selectedPestTypes: string[]
): DeviceDistributionDisplayDevice => {
    const supportsSpeciesRecognition = option.supports_species_recognition !== false
    const totalCount = option.total_count ?? sumCounts(option.pest_type_counts)
    const visibleCounts = filterPestCountsForDisplay(
        option.pest_type_counts,
        selectedPestTypes,
        supportsSpeciesRecognition
    )

    return {
        type: 'device',
        id: option.id,
        device_type: option.device_type,
        package_key: resolveDevicePackageKey(
            option.package_key,
            option.purchase_version,
            option.supports_species_recognition
        ),
        purchase_version: option.purchase_version,
        supports_species_recognition: supportsSpeciesRecognition,
        lng: option.lng,
        lat: option.lat,
        label: option.label,
        address: option.address,
        device_code: option.device_code,
        name: option.name,
        region_id: option.region_id,
        project_id: option.project_id,
        project_name: option.project_name,
        region_name: option.region_name,
        region_label: option.region_label,
        region_path_ids: option.region_path_ids,
        total_count: getVisibleMosquitoCount(
            option.pest_type_counts,
            totalCount,
            selectedPestTypes,
            supportsSpeciesRecognition
        ),
        pest_type_counts: visibleCounts,
        pest_types:
            selectedPestTypes.length > 0
                ? selectedPestTypes.filter((pestType) => visibleCounts[pestType] > 0)
                : option.pest_types,
        detection_count: option.detection_count ?? 0,
        last_detection_at: option.last_detection_at,
    }
}

const buildDeviceHistorySnapshot = (
    item: DeviceDistributionDisplayDevice
): DeviceHistorySnapshot => ({
    id: item.id,
    type: item.device_type ?? null,
    package_key: item.package_key ?? null,
    purchase_version: item.purchase_version ?? null,
    supports_species_recognition: item.supports_species_recognition ?? null,
    name: item.name ?? null,
    address: item.address ?? null,
    device_code: item.device_code ?? null,
    project_id: item.project_id ?? null,
})

const formatDeviceLastDetectionAt = (value?: string | null) =>
    value
        ? localeService.formatDate(value, {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '-'

const DeviceDistributionListItem = React.memo(
    ({
        item,
        isSelected,
        canLocateDevice,
        baseLabel,
        regionLabel,
        lastReportText,
        detectedCountText,
        locateText,
        noCoordinatesText,
        itemRef,
        onSelect,
    }: DeviceDistributionListItemProps) => (
        <button
            ref={itemRef}
            type="button"
            className={
                isSelected
                    ? 'device-distribution-list-item is-selected'
                    : 'device-distribution-list-item'
            }
            onClick={() => onSelect(item)}
        >
            <span className="device-distribution-list-item-top">
                <strong>{baseLabel}</strong>
                <span
                    className={
                        canLocateDevice
                            ? 'device-distribution-locate-icon'
                            : 'device-distribution-locate-icon is-disabled'
                    }
                    title={canLocateDevice ? locateText : noCoordinatesText}
                >
                    <AimOutlined />
                    <span className="device-distribution-locate-label">
                        {canLocateDevice ? locateText : noCoordinatesText}
                    </span>
                </span>
            </span>
            <span className="device-distribution-list-item-region">
                {regionLabel}
            </span>
            <span className="device-distribution-list-item-meta">
                <span>{lastReportText}</span>
                <span>{detectedCountText}</span>
            </span>
        </button>
    )
)

DeviceDistributionListItem.displayName = 'DeviceDistributionListItem'

interface VirtualDeviceDistributionListProps {
    rows: DeviceDistributionListRow[]
    selectedDeviceId: string | null
    selectedDeviceItemRef: React.Ref<HTMLButtonElement>
    locateText: string
    noCoordinatesText: string
    emptyText: React.ReactNode
    onSelect: (item: DeviceDistributionDisplayDevice) => void
}

const VirtualDeviceDistributionList = React.memo(
    ({
        rows,
        selectedDeviceId,
        selectedDeviceItemRef,
        locateText,
        noCoordinatesText,
        emptyText,
        onSelect,
    }: VirtualDeviceDistributionListProps) => {
        const listRef = useRef<HTMLDivElement | null>(null)
        const [scrollTop, setScrollTop] = useState(0)
        const viewportHeight = 420
        const selectedIndex = rows.findIndex(
            (row) => row.item.id === selectedDeviceId
        )

        useEffect(() => {
            const listElement = listRef.current
            if (!listElement || selectedIndex < 0) {
                return
            }

            const itemTop = selectedIndex * DEVICE_LIST_ROW_STRIDE
            const itemBottom = itemTop + DEVICE_LIST_ROW_HEIGHT
            const currentViewportHeight = listElement.clientHeight || 420
            const viewportTop = listElement.scrollTop
            const viewportBottom = viewportTop + currentViewportHeight

            if (itemTop < viewportTop) {
                listElement.scrollTop = itemTop
                setScrollTop(itemTop)
            } else if (itemBottom > viewportBottom) {
                const nextScrollTop = itemBottom - currentViewportHeight
                listElement.scrollTop = nextScrollTop
                setScrollTop(nextScrollTop)
            }
        }, [selectedIndex])

        const firstVisibleIndex = Math.max(
            0,
            Math.floor(scrollTop / DEVICE_LIST_ROW_STRIDE) - DEVICE_LIST_OVERSCAN
        )
        const lastVisibleIndex = Math.min(
            rows.length,
            Math.ceil(
                (scrollTop + viewportHeight) / DEVICE_LIST_ROW_STRIDE
            ) + DEVICE_LIST_OVERSCAN
        )
        const contentHeight = Math.max(
            0,
            rows.length * DEVICE_LIST_ROW_STRIDE - DEVICE_LIST_ROW_GAP
        )

        return (
            <div
                ref={listRef}
                className="device-distribution-list"
                onScroll={(event) =>
                    setScrollTop(event.currentTarget.scrollTop)
                }
            >
                {rows.length === 0 ? (
                    <div className="device-distribution-empty">{emptyText}</div>
                ) : (
                    <div
                        style={{
                            position: 'relative',
                            height: contentHeight,
                        }}
                    >
                        {rows
                            .slice(firstVisibleIndex, lastVisibleIndex)
                            .map((row, offset) => {
                                const index = firstVisibleIndex + offset
                                return (
                                    <div
                                        key={row.item.id}
                                        style={{
                                            position: 'absolute',
                                            top: index * DEVICE_LIST_ROW_STRIDE,
                                            right: 0,
                                            left: 0,
                                            height: DEVICE_LIST_ROW_HEIGHT,
                                        }}
                                    >
                                        <DeviceDistributionListItem
                                            {...row}
                                            itemRef={
                                                row.isSelected
                                                    ? selectedDeviceItemRef
                                                    : undefined
                                            }
                                            locateText={locateText}
                                            noCoordinatesText={noCoordinatesText}
                                            onSelect={onSelect}
                                        />
                                    </div>
                                )
                            })}
                    </div>
                )}
            </div>
        )
    }
)

VirtualDeviceDistributionList.displayName = 'VirtualDeviceDistributionList'

const DeviceMosquitoContent: React.FC = () => {
    const { t } = useTranslation('device')
    const location = useLocation()
    const { canAccessDeviceDistribution } = usePermissions()

    const mapRef = useRef<HTMLDivElement>(null)
    const mapInstanceRef = useRef<TiandituMap | null>(null)
    const markersRef = useRef<MarkerOverlay[]>([])
    const lastRouteRefreshTokenRef = useRef<unknown>(undefined)
    const filtersRef = useRef<FiltersState>(createDefaultFilters())
    const viewportRef = useRef<MapViewport>({
        zoom: DEFAULT_MAP_ZOOM,
    })
    const viewportRefreshTimerRef = useRef<ReturnType<
        typeof setTimeout
    > | null>(null)
    const markerRenderCancelRef = useRef<(() => void) | null>(null)
    const didInitializeDistributionRef = useRef(false)
    const mapInitializationPendingRef = useRef(false)
    const [map, setMap] = useState<TiandituMap | null>(null)
    const [mapStatus, setMapStatus] = useState<MapStatus>('loading')
    const [viewport, setViewport] = useState<MapViewport>({
        zoom: DEFAULT_MAP_ZOOM,
    })
    const [filters, setFilters] = useState<FiltersState>(() =>
        createDefaultFilters()
    )
    const [selectedProjectOptionIds, setSelectedProjectOptionIds] = useState<
        number[]
    >([])
    const [modalVisible, setModalVisible] = useState(false)
    const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(
        null
    )
    const [selectedDeviceItem, setSelectedDeviceItem] =
        useState<DeviceDistributionDisplayDevice | null>(null)
    const [selectedDeviceSource, setSelectedDeviceSource] =
        useState<DeviceSelectionSource | null>(null)
    const [isSelectedSummaryLocked, setIsSelectedSummaryLocked] = useState(
        false
    )
    const [selectedDeviceAddress, setSelectedDeviceAddress] = useState<
        string | undefined
    >(undefined)
    const [selectedDeviceSnapshot, setSelectedDeviceSnapshot] =
        useState<DeviceHistorySnapshot | null>(null)
    const [deviceKeyword, setDeviceKeyword] = useState('')
    const [debouncedDeviceKeyword, setDebouncedDeviceKeyword] = useState('')
    const selectedDeviceListItemRef = useRef<HTMLButtonElement | null>(null)
    const selectedDeviceIdRef = useRef<string | null>(null)
    const selectedSummaryLockedRef = useRef(false)
    const selectedSummaryPositionFrameRef = useRef<number | null>(null)
    const selectedSummaryPositionPendingRef = useRef(false)
    const viewportClampBypassTimerRef = useRef<ReturnType<
        typeof setTimeout
    > | null>(null)
    const dragGestureRef = useRef<MapDragGestureState | null>(null)
    const disabledMapDragPointerIdRef = useRef<number | null>(null)
    const pendingFlingRestoreRef = useRef<MapFlingRestoreTarget | null>(null)
    const didRequestDistributionOptionsRef = useRef(false)
    const [, refreshSelectedSummaryPosition] = useReducer(
        (value: number) => value + 1,
        0
    )

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedDeviceKeyword(deviceKeyword)
        }, DEVICE_LIST_SEARCH_DEBOUNCE_MS)

        return () => clearTimeout(timer)
    }, [deviceKeyword])

    const {
        distribution,
        fetchDistribution,
        error: distributionError,
    } = useDistributionData()
    const [manualRefreshLoading, setManualRefreshLoading] = useState(false)
    const {
        options: distributionOptions,
        error: distributionOptionsError,
        refreshOptions,
    } = useDistributionOptions({ initialLoad: false })
    const distributionOptionsRef = useRef<
        typeof distributionOptions | null
    >(distributionOptions)

    const refreshDistribution = useCallback(
        (
            forceRefresh = false,
            nextViewport = viewportRef.current,
            nextFilters = filtersRef.current
        ) =>
            fetchDistribution(
                buildDistributionQuery(nextFilters, nextViewport),
                forceRefresh
            ),
        [fetchDistribution]
    )
    const refreshDistributionRef = useRef(refreshDistribution)

    useEffect(() => {
        refreshDistributionRef.current = refreshDistribution
    }, [refreshDistribution])

    const refreshDistributionView = useCallback(
        async (
            nextViewport = viewportRef.current,
            nextFilters = filtersRef.current
        ) => {
            const [nextDistribution, nextOptions] = await Promise.all([
                refreshDistribution(true, nextViewport, nextFilters),
                refreshOptions(true),
            ])
            return { nextDistribution, nextOptions }
        },
        [refreshDistribution, refreshOptions]
    )

    useEffect(() => {
        distributionOptionsRef.current = distributionOptions
    }, [distributionOptions])

    useEffect(() => {
        if (
            didRequestDistributionOptionsRef.current ||
            distribution == null ||
            distributionOptions != null
        ) {
            return
        }

        didRequestDistributionOptionsRef.current = true
        let isCancelled = false

        const loadDistributionOptions = async () => {
            if (distributionOptionsRef.current != null) {
                return
            }
            try {
                await refreshOptions(false)
            } catch (error) {
                if (!isCancelled) {
                    console.error('加载设备分布筛选项失败:', error)
                }
            }
        }

        const idleWindow = window as Window & {
            requestIdleCallback?: (
                callback: () => void,
                options?: { timeout?: number }
            ) => number
            cancelIdleCallback?: (handle: number) => void
        }
        const requestId = idleWindow.requestIdleCallback
            ? idleWindow.requestIdleCallback(() => {
                  void loadDistributionOptions()
              })
            : window.setTimeout(() => {
                  void loadDistributionOptions()
              }, 0)

        return () => {
            isCancelled = true
            if (
                typeof requestId === 'number' &&
                typeof idleWindow.cancelIdleCallback === 'function' &&
                idleWindow.requestIdleCallback
            ) {
                idleWindow.cancelIdleCallback(requestId)
            } else {
                clearTimeout(requestId)
            }
        }
    }, [distribution, distributionOptions, refreshOptions])

    const updateViewportFromMap = useCallback(
        (forceRefresh = false, refreshWhenEqual = false) => {
            const nextViewport = readMapViewport(mapInstanceRef.current)
            if (
                !forceRefresh &&
                !refreshWhenEqual &&
                viewportsEqual(viewportRef.current, nextViewport)
            ) {
                return
            }
            viewportRef.current = nextViewport
            setViewport(nextViewport)
            void refreshDistributionRef.current(forceRefresh, nextViewport)
        },
        []
    )

    const scheduleViewportRefresh = useCallback(() => {
        if (viewportRefreshTimerRef.current) {
            clearTimeout(viewportRefreshTimerRef.current)
        }

        viewportRefreshTimerRef.current = setTimeout(() => {
            updateViewportFromMap(false)
        }, VIEWPORT_REFRESH_DEBOUNCE_MS)
    }, [updateViewportFromMap])

    const bypassNextViewportClamp = useCallback(() => {
        if (viewportClampBypassTimerRef.current) {
            clearTimeout(viewportClampBypassTimerRef.current)
        }

        pendingFlingRestoreRef.current = null
        viewportClampBypassTimerRef.current = setTimeout(() => {
            viewportClampBypassTimerRef.current = null
        }, 250)
    }, [])

    const settleMapViewport = useCallback(() => {
        if (mapInitializationPendingRef.current) {
            return
        }

        const mapInstance = mapInstanceRef.current

        if (!mapInstance) {
            scheduleViewportRefresh()
            return
        }

        if (viewportClampBypassTimerRef.current !== null) {
            scheduleViewportRefresh()
            return
        }

        const flingRestoreTarget = pendingFlingRestoreRef.current
        pendingFlingRestoreRef.current = null

        if (flingRestoreTarget) {
            mapInstance.centerAndZoom(
                flingRestoreTarget.center,
                flingRestoreTarget.zoom
            )
            updateViewportFromMap(false, true)
            return
        }

        scheduleViewportRefresh()
    }, [scheduleViewportRefresh, updateViewportFromMap])

    const startMapDragGesture = useCallback((event: PointerEvent) => {
        if (event.pointerType === 'mouse' && event.button !== 0) {
            return
        }
        if (event.isPrimary === false) {
            return
        }

        const mapInstance = mapInstanceRef.current
        const startCenter = getMapCenter(mapInstance)

        if (!mapInstance || !startCenter) {
            dragGestureRef.current = null
            pendingFlingRestoreRef.current = null
            return
        }

        dragGestureRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            lastX: event.clientX,
            lastY: event.clientY,
            startTime: Date.now(),
            startCenter,
            startZoom: mapInstance.getZoom?.() ?? DEFAULT_MAP_ZOOM,
        }
        pendingFlingRestoreRef.current = null
    }, [])

    const restoreMapDrag = useCallback((pointerId?: number) => {
        const disabledPointerId = disabledMapDragPointerIdRef.current
        if (
            disabledPointerId === null ||
            (pointerId !== undefined && disabledPointerId !== pointerId)
        ) {
            return
        }

        mapInstanceRef.current?.enableDrag?.()
        disabledMapDragPointerIdRef.current = null
    }, [])

    const stopMapDragGesture = useCallback((event: PointerEvent) => {
        const gesture = dragGestureRef.current

        if (
            event.pointerType !== 'mouse' ||
            !gesture ||
            gesture.pointerId !== event.pointerId
        ) {
            return
        }

        mapInstanceRef.current?.disableDrag?.()
        disabledMapDragPointerIdRef.current = event.pointerId
        dragGestureRef.current = null
        pendingFlingRestoreRef.current = null
    }, [])

    const updateMapDragGesture = useCallback((event: PointerEvent) => {
        const gesture = dragGestureRef.current

        if (!gesture || gesture.pointerId !== event.pointerId) {
            return
        }

        if (
            event.pointerType === 'mouse' &&
            isPointerOutsideElement(event, mapRef.current)
        ) {
            stopMapDragGesture(event)
            return
        }

        gesture.lastX = event.clientX
        gesture.lastY = event.clientY
    }, [stopMapDragGesture])

    const finishMapDragGesture = useCallback((event: PointerEvent) => {
        const gesture = dragGestureRef.current

        if (!gesture || gesture.pointerId !== event.pointerId) {
            restoreMapDrag(event.pointerId)
            return
        }

        if (
            event.pointerType === 'mouse' &&
            isPointerOutsideElement(event, mapRef.current)
        ) {
            stopMapDragGesture(event)
            restoreMapDrag(event.pointerId)
            return
        }

        updateMapDragGesture(event)
        dragGestureRef.current = null

        const mapInstance = mapInstanceRef.current
        if (!mapInstance || viewportClampBypassTimerRef.current !== null) {
            return
        }

        if (isLargeFlingGesture(gesture, Date.now())) {
            pendingFlingRestoreRef.current = {
                center: gesture.startCenter,
                zoom: mapInstance.getZoom?.() ?? gesture.startZoom,
            }
        }
        restoreMapDrag(event.pointerId)
    }, [
        restoreMapDrag,
        stopMapDragGesture,
        updateMapDragGesture,
    ])

    const cancelMapDragGesture = useCallback((event: PointerEvent) => {
        const gesture = dragGestureRef.current

        if (!gesture || gesture.pointerId !== event.pointerId) {
            restoreMapDrag(event.pointerId)
            return
        }

        dragGestureRef.current = null
        pendingFlingRestoreRef.current = null
        restoreMapDrag(event.pointerId)
    }, [restoreMapDrag])

    const handleMapPointerLeave = useCallback(
        (event: PointerEvent) => {
            stopMapDragGesture(event)
        },
        [stopMapDragGesture]
    )

    useEffect(() => {
        filtersRef.current = filters
        if (!didInitializeDistributionRef.current) {
            return
        }
        void refreshDistributionRef.current(false, viewportRef.current, filters)
    }, [filters])

    useEffect(() => {
        viewportRef.current = viewport
    }, [viewport])

    useEffect(() => {
        selectedDeviceIdRef.current = selectedDeviceId
    }, [selectedDeviceId])

    useEffect(() => {
        selectedSummaryLockedRef.current = isSelectedSummaryLocked
    }, [isSelectedSummaryLocked])

    useEffect(
        () => () => {
            if (selectedSummaryPositionFrameRef.current !== null) {
                cancelAnimationFrame(selectedSummaryPositionFrameRef.current)
                selectedSummaryPositionFrameRef.current = null
            }
            selectedSummaryPositionPendingRef.current = false
        },
        []
    )

    const scheduleSelectedSummaryPositionRefresh = useCallback(() => {
        if (selectedSummaryLockedRef.current) {
            return
        }

        if (selectedSummaryPositionPendingRef.current) {
            return
        }

        selectedSummaryPositionPendingRef.current = true
        selectedSummaryPositionFrameRef.current = requestAnimationFrame(() => {
            selectedSummaryPositionPendingRef.current = false
            selectedSummaryPositionFrameRef.current = null
            refreshSelectedSummaryPosition()
        })
    }, [refreshSelectedSummaryPosition])

    const releaseSelectedSummaryPositionLock = useCallback(() => {
        if (!selectedSummaryLockedRef.current) {
            return
        }

        selectedSummaryLockedRef.current = false
        setIsSelectedSummaryLocked(false)
    }, [])

    useEffect(() => {
        const refreshMapAt = (location.state as DeviceMosquitoLocationState | null)
            ?.refreshMapAt

        if (
            refreshMapAt == null ||
            lastRouteRefreshTokenRef.current === refreshMapAt ||
            !didInitializeDistributionRef.current
        ) {
            return
        }

        lastRouteRefreshTokenRef.current = refreshMapAt
        void refreshDistributionView()
    }, [location.state, refreshDistributionView])

    useEffect(
        () => () => {
            if (viewportRefreshTimerRef.current) {
                clearTimeout(viewportRefreshTimerRef.current)
            }
            if (viewportClampBypassTimerRef.current) {
                clearTimeout(viewportClampBypassTimerRef.current)
            }
            markerRenderCancelRef.current?.()
        },
        []
    )

    useEffect(() => {
        const currentMapContainer = mapRef.current

        if (!currentMapContainer) {
            return
        }

        currentMapContainer.addEventListener(
            'pointerdown',
            startMapDragGesture,
            true
        )
        currentMapContainer.addEventListener(
            'pointerleave',
            handleMapPointerLeave,
            true
        )
        window.addEventListener('pointermove', updateMapDragGesture, true)
        window.addEventListener('pointerup', finishMapDragGesture, true)
        window.addEventListener('pointercancel', cancelMapDragGesture, true)

        return () => {
            currentMapContainer.removeEventListener(
                'pointerdown',
                startMapDragGesture,
                true
            )
            currentMapContainer.removeEventListener(
                'pointerleave',
                handleMapPointerLeave,
                true
            )
            window.removeEventListener('pointermove', updateMapDragGesture, true)
            window.removeEventListener('pointerup', finishMapDragGesture, true)
            window.removeEventListener('pointercancel', cancelMapDragGesture, true)
            restoreMapDrag()
            dragGestureRef.current = null
            pendingFlingRestoreRef.current = null
        }
    }, [
        cancelMapDragGesture,
        finishMapDragGesture,
        handleMapPointerLeave,
        restoreMapDrag,
        startMapDragGesture,
        updateMapDragGesture,
    ])

    const optionDevices = useMemo(
        () => distributionOptions?.devices ?? [],
        [distributionOptions?.devices]
    )
    const selectedPestTypeProjectIds = useMemo(
        () =>
            optionDevices
                .filter((record) =>
                    matchesSelectedProjects(record.project_id, filters.projectIds)
                )
                .map((record) => record.project_id)
                .filter((projectId): projectId is number => projectId != null),
        [filters.projectIds, optionDevices]
    )
    const { options: configuredPestTypeOptions } = usePestTypeOptions(
        selectedPestTypeProjectIds
    )
    const selectedDistributionDeviceForPestTypes = useMemo(
        () =>
            selectedDeviceId
                ? ((distribution?.items ?? []).find(
                      (item) => isDeviceItem(item) && item.id === selectedDeviceId
                  ) as DeviceDistributionDeviceItem | undefined) ?? null
                : null,
        [distribution?.items, selectedDeviceId]
    )
    const selectedOptionDeviceForPestTypes = useMemo(
        () =>
            selectedDeviceId
                ? optionDevices.find((record) => record.id === selectedDeviceId) ??
                  null
                : null,
        [optionDevices, selectedDeviceId]
    )

    const projectOptions = useMemo(
        () =>
            (distributionOptions?.projects ?? [])
                .map((record) => ({
                    value: record.id,
                    label: record.label,
                    filterProjectIds:
                        record.filter_project_ids &&
                        record.filter_project_ids.length > 0
                            ? record.filter_project_ids
                            : [record.id],
                }))
                .sort((left, right) => left.label.localeCompare(right.label)),
        [distributionOptions?.projects]
    )

    const projectFilterIdsByOptionId = useMemo(
        () =>
            new Map(
                projectOptions.map((option) => [
                    option.value,
                    option.filterProjectIds,
                ])
            ),
        [projectOptions]
    )

    const projectSelectOptions = useMemo(
        () =>
            projectOptions.map((option) => ({
                value: option.value,
                label: option.label,
            })),
        [projectOptions]
    )

    const availableDeviceOptions = useMemo(
        () =>
            optionDevices
                .filter((record) => {
                    const supportsSpeciesRecognition =
                        record.supports_species_recognition !== false

                    if (
                        !matchesSelectedProjects(
                            record.project_id,
                            filters.projectIds
                        )
                    ) {
                        return false
                    }

                    if (filters.pestTypes.length > 0) {
                        if (
                            !supportsSpeciesRecognition &&
                            requiresSpeciesRecognitionFilter(filters.pestTypes)
                        ) {
                            return true
                        }

                        return (
                            getVisibleMosquitoCount(
                                record.pest_type_counts,
                                record.total_count ??
                                    sumCounts(record.pest_type_counts),
                                filters.pestTypes,
                                supportsSpeciesRecognition
                            ) > 0
                        )
                    }

                    return true
                })
                .map((record) => ({
                    value: record.id,
                    label: record.label,
                }))
                .sort((left, right) => left.label.localeCompare(right.label)),
        [filters.pestTypes, filters.projectIds, optionDevices]
    )

    const availablePestTypeOptions = useMemo(
        () => {
            const configuredLabels = configuredPestTypeOptions.map(
                (option) => option.label
            )
            if (selectedDeviceId) {
                const selectedDevicePestTypeSource =
                    filters.pestTypes.length === 0
                        ? selectedDistributionDeviceForPestTypes ??
                          selectedOptionDeviceForPestTypes
                        : selectedOptionDeviceForPestTypes ??
                          selectedDistributionDeviceForPestTypes

                return buildConfiguredPestTypeFilterOptions(
                    configuredLabels.length > 0
                        ? configuredLabels
                        : selectedDevicePestTypeSource?.pest_types ?? []
                )
            }

            return buildConfiguredPestTypeFilterOptions(
                configuredLabels.length > 0
                    ? configuredLabels
                    : optionDevices
                          .filter((record) => {
                        if (
                            !matchesSelectedProjects(
                                record.project_id,
                                filters.projectIds
                            )
                        ) {
                            return false
                        }

                        if (
                            filters.deviceIds.length > 0 &&
                            !filters.deviceIds.includes(record.id)
                        ) {
                            return false
                        }

                        return true
                          })
                          .flatMap((record) => record.pest_types)
            )
        },
        [
            filters.deviceIds,
            filters.pestTypes.length,
            filters.projectIds,
            configuredPestTypeOptions,
            optionDevices,
            selectedDeviceId,
            selectedDistributionDeviceForPestTypes,
            selectedOptionDeviceForPestTypes,
        ]
    )

    const availableProjectIds = useMemo(
        () => new Set(projectOptions.flatMap((item) => item.filterProjectIds)),
        [projectOptions]
    )
    const availableProjectOptionIds = useMemo(
        () => new Set(projectOptions.map((item) => item.value)),
        [projectOptions]
    )

    useEffect(() => {
        const availableDeviceIds = new Set(
            availableDeviceOptions.map((item) => item.value)
        )
        const availablePestTypes = new Set(
            availablePestTypeOptions.map((item) => item.value)
        )
        const nextProjectOptionIds = selectedProjectOptionIds.filter(
            (projectId) => availableProjectOptionIds.has(projectId)
        )
        const nextProjectIds = normalizeProjectFilterValues(
            nextProjectOptionIds,
            projectFilterIdsByOptionId
        ).filter((projectId) => availableProjectIds.has(projectId))

        if (!arraysEqual(selectedProjectOptionIds, nextProjectOptionIds)) {
            setSelectedProjectOptionIds(nextProjectOptionIds)
        }

        setFilters((previousFilters) => {
            const nextDeviceIds = previousFilters.deviceIds.filter((deviceId) =>
                availableDeviceIds.has(deviceId)
            )
            const nextPestTypes = previousFilters.pestTypes.filter((pestType) =>
                availablePestTypes.has(pestType)
            )

            if (
                arraysEqual(previousFilters.projectIds, nextProjectIds) &&
                arraysEqual(previousFilters.deviceIds, nextDeviceIds) &&
                arraysEqual(previousFilters.pestTypes, nextPestTypes)
            ) {
                return previousFilters
            }

            return {
                ...previousFilters,
                projectIds: nextProjectIds,
                deviceIds: nextDeviceIds,
                pestTypes: nextPestTypes,
            }
        })
    }, [
        availableDeviceOptions,
        availablePestTypeOptions,
        availableProjectIds,
        availableProjectOptionIds,
        projectFilterIdsByOptionId,
        selectedProjectOptionIds,
    ])

    const normalizedDeviceKeyword = useMemo(
        () => normalizeDeviceSearchText(debouncedDeviceKeyword),
        [debouncedDeviceKeyword]
    )

    const validDistributionItems = useMemo(
        () => (distribution?.items ?? []).filter(hasValidCoordinates),
        [distribution?.items]
    )
    const visibleItems = useMemo(() => {
        if (!selectedDeviceId) {
            return validDistributionItems
        }

        return [
            ...validDistributionItems.filter(
                (item) => !isDeviceItem(item) || item.id !== selectedDeviceId
            ),
            ...validDistributionItems.filter(
                (item) => isDeviceItem(item) && item.id === selectedDeviceId
            ),
        ]
    }, [selectedDeviceId, validDistributionItems])
    const visibleMapDeviceItems = useMemo(
        () => validDistributionItems.filter(isDeviceItem),
        [validDistributionItems]
    )
    const searchableDeviceItems = useMemo(
        () =>
            optionDevices
                .filter((record) => {
                    const supportsSpeciesRecognition =
                        record.supports_species_recognition !== false

                    if (
                        !matchesSelectedProjects(
                            record.project_id,
                            filters.projectIds
                        )
                    ) {
                        return false
                    }

                    if (
                        filters.deviceIds.length > 0 &&
                        !filters.deviceIds.includes(record.id)
                    ) {
                        return false
                    }

                    if (filters.pestTypes.length > 0) {
                        if (
                            !supportsSpeciesRecognition &&
                            requiresSpeciesRecognitionFilter(filters.pestTypes)
                        ) {
                            return true
                        }

                        return (
                            getVisibleMosquitoCount(
                                record.pest_type_counts,
                                record.total_count ??
                                    sumCounts(record.pest_type_counts),
                                filters.pestTypes,
                                supportsSpeciesRecognition
                            ) > 0
                        )
                    }

                    return true
                })
                .map((record) => buildDeviceFromOption(record, filters.pestTypes)),
        [filters.deviceIds, filters.pestTypes, filters.projectIds, optionDevices]
    )
    const listSourceDeviceItems =
        distributionOptions == null && searchableDeviceItems.length === 0
            ? visibleMapDeviceItems
            : searchableDeviceItems
    const visibleDeviceItems = useMemo(() => {
        if (!normalizedDeviceKeyword) {
            return listSourceDeviceItems
        }

        return listSourceDeviceItems.filter((item) =>
            deviceMatchesKeyword(item, normalizedDeviceKeyword)
        )
    }, [listSourceDeviceItems, normalizedDeviceKeyword])
    const isSelectedDeviceVisible = useMemo(
        () =>
            selectedDeviceId
                ? selectedDeviceSource === 'map'
                    ? visibleMapDeviceItems.some(
                          (item) => item.id === selectedDeviceId
                      )
                    : visibleDeviceItems.some(
                          (item) => item.id === selectedDeviceId
                      )
                : false,
        [
            selectedDeviceId,
            selectedDeviceSource,
            visibleDeviceItems,
            visibleMapDeviceItems,
        ]
    )
    const visibleDeviceCount = visibleDeviceItems.length
    const visibleDeviceListRows = useMemo<DeviceDistributionListRow[]>(
        () =>
            visibleDeviceItems.map((item) => {
                const lastReportTime = formatDeviceLastDetectionAt(
                    item.last_detection_at
                )

                return {
                    item,
                    isSelected: item.id === selectedDeviceId,
                    canLocateDevice: hasValidDeviceCoordinates(item),
                    baseLabel: getDeviceBaseLabel(item),
                    regionLabel:
                        item.region_label ||
                        item.region_name ||
                        item.project_name ||
                        '-',
                    lastReportText: t('mosquito.lastReport', {
                        time: lastReportTime,
                        defaultValue: `最近上报 ${lastReportTime}`,
                    }),
                    detectedCountText: t('mosquito.detectedCount', {
                        count: item.total_count,
                        defaultValue: `识别 ${item.total_count} 只`,
                    }),
                }
            }),
        [selectedDeviceId, t, visibleDeviceItems]
    )
    const deviceById = useMemo(
        () => {
            const sourceItems =
                filters.pestTypes.length > 0
                    ? [...visibleMapDeviceItems, ...searchableDeviceItems]
                    : [...searchableDeviceItems, ...visibleMapDeviceItems]
            return new Map(sourceItems.map((item) => [item.id, item]))
        },
        [filters.pestTypes.length, searchableDeviceItems, visibleMapDeviceItems]
    )
    const selectedDevice = useMemo(
        () =>
            selectedDeviceId
                ? deviceById.get(selectedDeviceId) ??
                  (selectedDeviceItem?.id === selectedDeviceId
                      ? selectedDeviceItem
                      : null)
                : null,
        [deviceById, selectedDeviceId, selectedDeviceItem]
    )
    const selectedDeviceForSummary = isSelectedDeviceVisible
        ? selectedDevice
        : null
    const selectedSummaryPosition = getSelectedSummaryPosition(
        isSelectedSummaryLocked ? null : map,
        mapRef.current,
        selectedDeviceForSummary
    )
    useEffect(() => {
        selectedDeviceListItemRef.current?.scrollIntoView?.({
            block: 'nearest',
        })
    }, [selectedDeviceId])

    useEffect(() => {
        let isCancelled = false
        const currentMapContainer = mapRef.current

        const initializeMap = async () => {
            try {
                setMapStatus('loading')
                await loadTiandituApi()

                if (isCancelled) {
                    return
                }

                await new Promise<void>((resolve) => {
                    requestAnimationFrame(() => resolve())
                })

                if (isCancelled) {
                    return
                }

                const tianditu = getTianditu()
                if (!tianditu || !currentMapContainer) {
                    setMapStatus('error')
                    return
                }

                currentMapContainer.innerHTML = ''

                const mapInstance = new tianditu.Map(currentMapContainer)
                const centerPoint = createDefaultMapCenterPoint(tianditu)
                mapInitializationPendingRef.current = true
                bypassNextViewportClamp()
                mapInstance.centerAndZoom(centerPoint, DEFAULT_MAP_ZOOM)
                mapInstance.addControl(createMapTypeControl(tianditu))
                mapInstance.addControl(new tianditu.Control.Zoom())
                enableMapInteractions(mapInstance)
                disableMapFlingInertia(mapInstance)
                syncMapContainerSize(mapInstance)

                mapInstance.addEventListener?.(
                    'moveend',
                    () => {
                        releaseSelectedSummaryPositionLock()
                        settleMapViewport()
                    }
                )
                mapInstance.addEventListener?.(
                    'zoomend',
                    () => {
                        releaseSelectedSummaryPositionLock()
                        settleMapViewport()
                    }
                )
                mapInstance.addEventListener?.(
                    'move',
                    scheduleSelectedSummaryPositionRefresh
                )
                mapInstance.addEventListener?.(
                    'zoom',
                    scheduleSelectedSummaryPositionRefresh
                )
                mapInstance.addEventListener?.(
                    'resize',
                    scheduleSelectedSummaryPositionRefresh
                )

                mapInstanceRef.current = mapInstance
                setMap(mapInstance)
                setMapStatus('ready')
                const nextViewport = readMapViewport(mapInstance)
                viewportRef.current = nextViewport
                setViewport(nextViewport)
                didInitializeDistributionRef.current = true
                void refreshDistributionRef.current(
                    true,
                    nextViewport,
                    filtersRef.current
                )
                setTimeout(() => {
                    if (!isCancelled) {
                        mapInitializationPendingRef.current = false
                    }
                }, 0)
            } catch (error) {
                console.error('Error loading Tianditu API:', error)
                if (!isCancelled) {
                    mapInitializationPendingRef.current = false
                    setMapStatus('error')
                }
            }
        }

        void initializeMap()

        return () => {
            isCancelled = true
            markerRenderCancelRef.current?.()
            markerRenderCancelRef.current = null
            markersRef.current = []
            mapInstanceRef.current = null
            didInitializeDistributionRef.current = false
            mapInitializationPendingRef.current = false
            setMap(null)
            if (currentMapContainer) {
                currentMapContainer.innerHTML = ''
            }
        }
    }, [
        bypassNextViewportClamp,
        releaseSelectedSummaryPositionLock,
        scheduleSelectedSummaryPositionRefresh,
        scheduleViewportRefresh,
        settleMapViewport,
    ])

    useEffect(() => {
        const currentMapContainer = mapRef.current

        if (!map || !currentMapContainer) {
            return
        }

        let animationFrameId: number | null = null

        const scheduleMapSizeSync = () => {
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId)
            }

            animationFrameId = requestAnimationFrame(() => {
                animationFrameId = null
                syncMapContainerSize(map)
                scheduleSelectedSummaryPositionRefresh()
            })
        }

        scheduleMapSizeSync()

        const resizeObserver =
            typeof ResizeObserver === 'function'
                ? new ResizeObserver(scheduleMapSizeSync)
                : null
        resizeObserver?.observe(currentMapContainer)
        window.addEventListener('resize', scheduleMapSizeSync)

        return () => {
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId)
            }
            resizeObserver?.disconnect()
            window.removeEventListener('resize', scheduleMapSizeSync)
        }
    }, [map, scheduleSelectedSummaryPositionRefresh])

    const focusDeviceOnMap = useCallback(
        (item: DeviceDistributionDisplayDevice) => {
            if (!map || !hasValidDeviceCoordinates(item)) {
                return false
            }

            selectedSummaryLockedRef.current = true
            setIsSelectedSummaryLocked(true)
            syncMapContainerSize(map)
            bypassNextViewportClamp()
            map.centerAndZoom(
                { lng: item.lng, lat: item.lat },
                Math.max(viewport.zoom ?? DEFAULT_MAP_ZOOM, DEVICE_FOCUS_ZOOM)
            )
            return true
        },
        [bypassNextViewportClamp, map, viewport.zoom]
    )

    const updateSelectedDeviceData = useCallback(
        (
            item: DeviceDistributionDisplayDevice,
            source?: DeviceSelectionSource
        ) => {
            selectedDeviceIdRef.current = item.id
            setSelectedDeviceId(item.id)
            setSelectedDeviceItem(item)
            if (source) {
                setSelectedDeviceSource(source)
            }
            setSelectedDeviceAddress(getDeviceBaseLabel(item))
            setSelectedDeviceSnapshot(buildDeviceHistorySnapshot(item))
        },
        []
    )

    const applyDeviceSelection = useCallback(
        (
            item: DeviceDistributionDisplayDevice,
            openHistory = false,
            source?: DeviceSelectionSource
        ) => {
            updateSelectedDeviceData(item, source)
            focusDeviceOnMap(item)
            if (openHistory) {
                setModalVisible(true)
            }
        },
        [focusDeviceOnMap, updateSelectedDeviceData]
    )

    const findRefreshedDevice = useCallback(
        (
            deviceId: string,
            nextDistribution: typeof distribution,
            nextOptions: typeof distributionOptions
        ): DeviceDistributionDisplayDevice | null => {
            const distributionItem = nextDistribution?.items.find(
                (item) => isDeviceItem(item) && item.id === deviceId
            )
            if (distributionItem && isDeviceItem(distributionItem)) {
                return distributionItem
            }

            const optionDevice = nextOptions?.devices.find(
                (item) => item.id === deviceId
            )
            return optionDevice
                ? buildDeviceFromOption(optionDevice, filtersRef.current.pestTypes)
                : null
        },
        []
    )

    const refreshSelectedDevice = useCallback(
        async (deviceId: string) => {
            const { nextDistribution, nextOptions } =
                await refreshDistributionView()
            const refreshedDevice = findRefreshedDevice(
                deviceId,
                nextDistribution,
                nextOptions
            )
            if (
                refreshedDevice &&
                selectedDeviceIdRef.current === refreshedDevice.id
            ) {
                updateSelectedDeviceData(refreshedDevice)
            }
            return refreshedDevice
        },
        [findRefreshedDevice, refreshDistributionView, updateSelectedDeviceData]
    )

    const handleDeviceListSelect = useCallback(
        (item: DeviceDistributionDisplayDevice) => {
            applyDeviceSelection(item, false, 'list')
            void refreshSelectedDevice(item.id)
        },
        [applyDeviceSelection, refreshSelectedDevice]
    )

    const handleMarkerClick = useCallback(
        (item: DeviceDistributionDeviceItem | DeviceDistributionClusterItem) => {
            if (!isDeviceItem(item)) {
                return
            }

            applyDeviceSelection(item, false, 'map')
        },
        [applyDeviceSelection]
    )

    const clearSelectedDeviceState = useCallback(() => {
        selectedDeviceIdRef.current = null
        selectedSummaryLockedRef.current = false
        setModalVisible(false)
        setIsSelectedSummaryLocked(false)
        setSelectedDeviceId(null)
        setSelectedDeviceItem(null)
        setSelectedDeviceSource(null)
        setSelectedDeviceAddress(undefined)
        setSelectedDeviceSnapshot(null)
    }, [])

    useEffect(() => {
        if (selectedDeviceId && !isSelectedDeviceVisible) {
            clearSelectedDeviceState()
        }
    }, [
        clearSelectedDeviceState,
        isSelectedDeviceVisible,
        selectedDeviceId,
    ])

    const handleModalClose = () => {
        const deviceIdToRefresh = selectedDeviceIdRef.current
        setModalVisible(false)
        if (deviceIdToRefresh) {
            void refreshSelectedDevice(deviceIdToRefresh)
        }
    }

    const handleSummaryClose = clearSelectedDeviceState

    const resetMapViewport = useCallback(() => {
        const mapInstance = mapInstanceRef.current
        const tianditu = getTianditu()

        if (!mapInstance || !tianditu) {
            return null
        }

        bypassNextViewportClamp()
        mapInstance.centerAndZoom(
            createDefaultMapCenterPoint(tianditu),
            DEFAULT_MAP_ZOOM
        )
        syncMapContainerSize(mapInstance)

        const nextViewport = readMapViewport(mapInstance)
        viewportRef.current = nextViewport
        setViewport(nextViewport)

        return nextViewport
    }, [bypassNextViewportClamp])

    const handleOpenSelectedDeviceHistory = () => {
        if (selectedDeviceId) {
            const deviceIdToOpen = selectedDeviceId
            if (selectedDevice) {
                applyDeviceSelection(selectedDevice)
            }
            setModalVisible(true)
            void refreshSelectedDevice(deviceIdToOpen)
        }
    }

    const getMapLabel = useCallback(
        (item: DeviceDistributionItem) => {
            if (isDeviceItem(item)) {
                const label = `${getDeviceBaseLabel(item)} ${t(
                    'mosquito.mosquitoKilled',
                    {
                        count: item.total_count,
                    }
                )}`
                return label
            }

            if (item.type === 'region_cluster' && item.region_label) {
                return t('mosquito.regionClusterLabel', {
                    region: item.region_label,
                    deviceCount: item.device_count,
                    total: item.total_count,
                })
            }

            return t('mosquito.clusterLabel', {
                deviceCount: item.device_count,
                total: item.total_count,
            })
        },
        [t]
    )
    const getMapLabelRef = useRef(getMapLabel)

    useEffect(() => {
        getMapLabelRef.current = getMapLabel
    }, [getMapLabel])

    useEffect(() => {
        const tianditu = getTianditu()

        if (!map || !tianditu) {
            return
        }

        const previousMarkers = markersRef.current

        markerRenderCancelRef.current?.()
        markerRenderCancelRef.current = null

        if (!visibleItems.length) {
            previousMarkers.forEach((markerOverlay) => {
                map.removeOverLay(markerOverlay.marker)
                if (markerOverlay.label) {
                    map.removeOverLay(markerOverlay.label)
                }
            })
            markersRef.current = []
            return
        }

        const newMarkers: MarkerOverlay[] = []
        let isCancelled = false
        let hasCommittedMarkers = false
        let nextIndex = 0
        let timeoutId: ReturnType<typeof setTimeout> | null = null
        let idleId: number | null = null

        const cancelScheduledRender = () => {
            if (isCancelled) {
                return
            }
            isCancelled = true
            if (timeoutId !== null) {
                clearTimeout(timeoutId)
            }
            if (
                idleId !== null &&
                typeof window.cancelIdleCallback === 'function'
            ) {
                window.cancelIdleCallback(idleId)
            }
            if (!hasCommittedMarkers) {
                newMarkers.forEach((markerOverlay) => {
                    map.removeOverLay(markerOverlay.marker)
                    if (markerOverlay.label) {
                        map.removeOverLay(markerOverlay.label)
                    }
                })
            }
        }

        const scheduleNextBatch = () => {
            if (isCancelled || nextIndex >= visibleItems.length) {
                if (!isCancelled) {
                    enableMapInteractions(map)
                    previousMarkers.forEach((markerOverlay) => {
                        map.removeOverLay(markerOverlay.marker)
                        if (markerOverlay.label) {
                            map.removeOverLay(markerOverlay.label)
                        }
                    })
                    markersRef.current = newMarkers
                    hasCommittedMarkers = true
                    markerRenderCancelRef.current = null
                }
                return
            }

            const renderBatch = () => {
                if (isCancelled) {
                    return
                }

                const batchEnd = Math.min(
                    nextIndex + MARKER_RENDER_BATCH_SIZE,
                    visibleItems.length
                )

                for (; nextIndex < batchEnd; nextIndex += 1) {
                    const item = visibleItems[nextIndex]
                    const point = new tianditu.LngLat(item.lng, item.lat)
                    const isSelectedMapDevice =
                        isDeviceItem(item) && item.id === selectedDeviceId
                    const selectedIcon = isSelectedMapDevice
                        ? createSelectedDeviceMarkerIcon(tianditu)
                        : undefined
                    const marker = new tianditu.Marker(
                        point,
                        selectedIcon ? { icon: selectedIcon } : undefined
                    )
                    if (isSelectedMapDevice) {
                        marker.setZIndexOffset?.(SELECTED_DEVICE_MARKER_Z_INDEX)
                    }

                    if (canAccessDeviceDistribution && isDeviceItem(item)) {
                        marker.addEventListener('click', () =>
                            handleMarkerClick(item)
                        )
                    }

                    const label =
                        typeof tianditu.Label === 'function'
                            ? new tianditu.Label({
                                  text: getMapLabelRef.current(item),
                                  position: point,
                                  offset: new tianditu.Point(0, -45),
                              })
                            : null
                    map.addOverLay(marker)
                    if (label) {
                        if (canAccessDeviceDistribution && isDeviceItem(item)) {
                            label.addEventListener?.('click', () =>
                                handleMarkerClick(item)
                            )
                        }
                        if (isSelectedMapDevice) {
                            label.setStyle?.({
                                backgroundColor: '#fff7ed',
                                border: '1px solid #ff9f43',
                                borderRadius: '4px',
                                color: '#7c2d12',
                                fontWeight: 700,
                                padding: '4px 8px',
                                boxShadow: '0 8px 18px rgba(255, 122, 0, 0.2)',
                                zIndex: SELECTED_DEVICE_MARKER_Z_INDEX - 1,
                            })
                        }
                        map.addOverLay(label)
                    }

                    newMarkers.push({ marker, label })
                }

                scheduleNextBatch()
            }

            if (typeof window.requestIdleCallback === 'function') {
                idleId = window.requestIdleCallback(renderBatch)
                return
            }

            timeoutId = setTimeout(renderBatch, 0)
        }

        markerRenderCancelRef.current = cancelScheduledRender
        scheduleNextBatch()

        return cancelScheduledRender
    }, [
        canAccessDeviceDistribution,
        handleMarkerClick,
        map,
        selectedDeviceId,
        visibleItems,
    ])

    const handleRefresh = async () => {
        setManualRefreshLoading(true)
        try {
            clearSelectedDeviceState()
            const nextViewport = resetMapViewport()
            await refreshDistributionView(nextViewport ?? viewportRef.current)
        } finally {
            setManualRefreshLoading(false)
        }
    }

    const handleResetFilters = () => {
        clearSelectedDeviceState()
        setDeviceKeyword('')
        setSelectedProjectOptionIds((previousProjectOptionIds) =>
            previousProjectOptionIds.length === 0
                ? previousProjectOptionIds
                : []
        )
        setFilters((previousFilters) =>
            arraysEqual(previousFilters.projectIds, []) &&
            arraysEqual(previousFilters.deviceIds, []) &&
            arraysEqual(previousFilters.pestTypes, [])
                ? previousFilters
                : createDefaultFilters()
        )
    }

    const locateText = t('mosquito.locateDevice', {
        defaultValue: '定位设备',
    })
    const noCoordinatesText = t('mosquito.noCoordinates', {
        defaultValue: '未配置坐标',
    })
    const distributionLoadError =
        distributionError || distributionOptionsError || null

    return (
        <PageLayout
            title={t('mosquito.title')}
            breadcrumb={[
                { title: t('navigation:breadcrumb.dataCenter') },
                { title: t('navigation:breadcrumb.deviceMosquito') },
            ]}
        >
            <div className="device-distribution-layout">
                <div
                    data-testid="device-distribution-map"
                    className="device-distribution-map-shell"
                    style={{
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        zIndex: 0,
                        isolation: 'isolate',
                        overflow: 'hidden',
                        touchAction: 'none',
                    }}
                >
                    <div
                        ref={mapRef}
                        className="device-distribution-map-canvas"
                        style={{
                            width: '100%',
                            height: '100%',
                        }}
                    />
                    {distributionLoadError ? (
                        <div
                            className="device-distribution-rebuilding-state"
                            role="alert"
                        >
                            <span>{distributionLoadError}</span>
                            <Button
                                icon={<ReloadOutlined />}
                                loading={manualRefreshLoading}
                                onClick={() => void handleRefresh()}
                            >
                                {t('mosquito.refreshData')}
                            </Button>
                        </div>
                    ) : null}
                    {selectedDeviceForSummary && !isSelectedSummaryLocked && (
                    <div
                        className={[
                            'device-distribution-map-summary',
                            selectedSummaryPosition
                                ? `is-${selectedSummaryPosition.placement}`
                                : 'is-default',
                        ].join(' ')}
                        style={
                            selectedSummaryPosition
                                ? {
                                      left: selectedSummaryPosition.left,
                                      top: selectedSummaryPosition.top,
                                  }
                                : undefined
                        }
                    >
                        <div className="device-distribution-map-summary-header">
                            <strong>{getDeviceBaseLabel(selectedDeviceForSummary)}</strong>
                            <button
                                type="button"
                                aria-label={t('mosquito.closeSummary', {
                                    defaultValue: '关闭',
                                })}
                                onClick={handleSummaryClose}
                            >
                                ×
                            </button>
                        </div>
                        <div className="device-distribution-map-summary-row">
                            <span>
                                {t('mosquito.summaryKilled', {
                                    defaultValue: '灭蚊数量',
                                })}
                            </span>
                            <strong>
                                {t('mosquito.summaryKilledValue', {
                                    count: selectedDeviceForSummary.total_count,
                                    defaultValue: `${selectedDeviceForSummary.total_count} 只`,
                                })}
                            </strong>
                        </div>
                        <div className="device-distribution-map-summary-row">
                            <span>
                                {t('mosquito.summaryLastReport', {
                                    defaultValue: '近期上报时间',
                                })}
                            </span>
                            <strong>
                                {selectedDeviceForSummary.last_detection_at
                                    ? localeService.formatDate(
                                          selectedDeviceForSummary.last_detection_at,
                                          {
                                              year: 'numeric',
                                              month: '2-digit',
                                              day: '2-digit',
                                              hour: '2-digit',
                                              minute: '2-digit',
                                          }
                                      )
                                    : '-'}
                            </strong>
                        </div>
                        {!hasValidDeviceCoordinates(selectedDeviceForSummary) && (
                            <div className="device-distribution-map-summary-row device-distribution-map-summary-row-warning">
                                <span>
                                    {t('mosquito.summaryMapLocation', {
                                        defaultValue: '地图位置',
                                    })}
                                </span>
                                <strong>
                                    {t('mosquito.noCoordinates', {
                                        defaultValue: '未配置坐标',
                                    })}
                                </strong>
                            </div>
                        )}
                        <button
                            type="button"
                            className="device-distribution-history-button"
                            onClick={handleOpenSelectedDeviceHistory}
                        >
                            {t('mosquito.viewHistory', {
                                defaultValue: '查看历史记录',
                            })}
                        </button>
                    </div>
                )}
                {mapStatus !== 'ready' && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fafafa',
                            zIndex: 1,
                        }}
                    >
                        {mapStatus === 'error' ? (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 12,
                                    color: '#595959',
                                }}
                            >
                                <span>{t('mosquito.mapLoadFailed')}</span>
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => window.location.reload()}
                                >
                                    {t('mosquito.refreshData')}
                                </Button>
                            </div>
                        ) : (
                            <Spin>
                                <span>{t('mosquito.loading')}</span>
                            </Spin>
                        )}
                    </div>
                    )}
                </div>

                <aside className="device-distribution-panel">
                    <div className="device-distribution-panel-title">
                        {t('mosquito.searchTitle', {
                            defaultValue: '设备检索',
                        })}
                    </div>
                    <Input
                        allowClear
                        className="device-distribution-keyword-input"
                        prefix={<SearchOutlined />}
                        value={deviceKeyword}
                        placeholder={t('mosquito.keywordPlaceholder')}
                        onChange={(event) => setDeviceKeyword(event.target.value)}
                    />
                    <FilterBar className="device-distribution-filter-panel">
                        <FilterField label={t('mosquito.filters.project')}>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                value={getAllAwareDisplayValues(
                                    selectedProjectOptionIds
                                )}
                                options={buildAllFilterOptions(
                                    t('mosquito.filters.allProject'),
                                    projectSelectOptions
                                )}
                                placeholder={t('mosquito.filters.projectPlaceholder')}
                                optionFilterProp="label"
                                maxTagCount="responsive"
                                onChange={(value) => {
                                    const nextProjectOptionIds =
                                        normalizeAllAwareValues(
                                            value as FilterSelectValue<number>[],
                                            selectedProjectOptionIds
                                        )
                                    setSelectedProjectOptionIds(nextProjectOptionIds)
                                    setFilters((previousFilters) => ({
                                        ...previousFilters,
                                        projectIds: normalizeProjectFilterValues(
                                            nextProjectOptionIds,
                                            projectFilterIdsByOptionId
                                        ),
                                    }))
                                }}
                            />
                        </FilterField>

                        <FilterField label={t('mosquito.filters.device')}>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                value={getAllAwareDisplayValues(filters.deviceIds)}
                                options={buildAllFilterOptions(
                                    t('mosquito.filters.allDevice'),
                                    availableDeviceOptions
                                )}
                                placeholder={t('mosquito.filters.devicePlaceholder')}
                                optionFilterProp="label"
                                maxTagCount="responsive"
                                onChange={(value) =>
                                    setFilters((previousFilters) => ({
                                        ...previousFilters,
                                        deviceIds: normalizeAllAwareValues(
                                            value as FilterSelectValue<string>[],
                                            previousFilters.deviceIds
                                        ),
                                    }))
                                }
                            />
                        </FilterField>

                        <FilterField label={t('mosquito.filters.pestType')}>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                value={getAllAwareDisplayValues(filters.pestTypes)}
                                options={buildAllFilterOptions(
                                    t('mosquito.filters.allPestType'),
                                    availablePestTypeOptions
                                )}
                                placeholder={t('mosquito.filters.pestTypePlaceholder')}
                                optionFilterProp="label"
                                maxTagCount="responsive"
                                onChange={(value) =>
                                    setFilters((previousFilters) => ({
                                        ...previousFilters,
                                        pestTypes: normalizeAllAwareValues(
                                            value as FilterSelectValue<string>[],
                                            previousFilters.pestTypes
                                        ),
                                    }))
                                }
                            />
                        </FilterField>

                        <FilterActions>
                            <Button onClick={handleResetFilters}>
                                {t('mosquito.filters.reset')}
                            </Button>
                            <Button
                                onClick={() => void handleRefresh()}
                                disabled={manualRefreshLoading}
                                icon={<ReloadOutlined />}
                                loading={manualRefreshLoading}
                            >
                                {t('mosquito.refreshData')}
                            </Button>
                        </FilterActions>
                    </FilterBar>

                    <div className="device-distribution-list-header">
                        <strong>
                            {t('mosquito.deviceList', {
                                defaultValue: '设备列表',
                            })}
                        </strong>
                        <span>
                            {t('mosquito.devicesFound', {
                                count: visibleDeviceCount,
                            })}
                        </span>
                    </div>
                    <VirtualDeviceDistributionList
                        rows={visibleDeviceListRows}
                        selectedDeviceId={selectedDeviceId}
                        selectedDeviceItemRef={selectedDeviceListItemRef}
                        locateText={locateText}
                        noCoordinatesText={noCoordinatesText}
                        emptyText={
                            normalizedDeviceKeyword
                                ? t('mosquito.noKeywordDevices')
                                : t('mosquito.noVisibleDevices', {
                                      defaultValue: '当前筛选条件下暂无设备',
                                  })
                        }
                        onSelect={handleDeviceListSelect}
                    />
                </aside>
            </div>

            {canAccessDeviceDistribution && (
                <DeviceHistoryModal
                    visible={modalVisible}
                    deviceId={selectedDeviceId}
                    deviceAddress={selectedDeviceAddress}
                    deviceSnapshot={selectedDeviceSnapshot}
                    onClose={handleModalClose}
                />
            )}
        </PageLayout>
    )
}

const DeviceMosquito: React.FC = () => {
    return <DeviceMosquitoContent />
}

export default DeviceMosquito
